// Takvim üstü "Bugün Sahada" paneli: bugünkü onaylı işler, lokasyona göre gruplu
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, HardHat, MapPin, Users, Truck, Siren } from 'lucide-react';
import { HAZARD } from './wizard/HazardClassPicker';
import { iso } from '../settings/calendarViews';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const no = (ev) => (ev.request_number ? `#${ev.request_number}` : 'No yok');
export const jobType = (ev) => [ev.is_tipi, ev.is_cesidi].filter(Boolean).join(' / ') || 'İş tipi belirtilmemiş';
export const HazardTag = ({ ev }) => {
  const list = [ev.hazard_class, ev.equipment_hazard_class].filter(Boolean);
  if (!list.length) return <span className="mr-1 rounded bg-white/90 px-1 text-[10px] font-bold text-slate-500" title="Tehlike sınıfı belirtilmemiş">?</span>;
  return list.map((h, i) => <span key={i} className="mr-1 rounded bg-white px-1 text-[10px] font-bold" style={{ color: HAZARD[h]?.color }} title={`${i ? 'Ekipman firması: ' : ''}${HAZARD[h]?.label}`}>{HAZARD[h]?.short}</span>);
};

const mins = (t) => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + (m || 0); };
export const PHASE = { wait: ['Başlamadı', 'bg-slate-100 text-slate-600'], run: ['Devam ediyor', 'bg-emerald-100 text-emerald-700'], done: ['Bitti', 'bg-slate-200 text-slate-500'] };
export const phaseOf = (ev, today, now) => {
  const s = mins(ev.start_time), e = mins(ev.end_time);
  if (e > s) return now < s ? 'wait' : now < e ? 'run' : 'done';
  // Gece çalışması: önceki günden süren sabah kısmı + bugünün akşam başlangıcı
  if (today > ev.start_date && now < e) return 'run';
  if (now >= s && today <= ev.end_date) return 'run';
  return today === ev.end_date && today > ev.start_date && now >= e ? 'done' : 'wait';
};

const Row = ({ ev, phase, onView }) => (
  <button type="button" onClick={() => onView(ev)} data-testid={`today-row-${ev.id}`}
    className={`grid w-full grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5 px-4 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 sm:grid-cols-[150px_110px_1fr_auto] sm:gap-3 ${phase === 'done' ? 'opacity-60' : ''}`}>
    <span className="flex items-center gap-1 font-semibold text-slate-900"><span className="rounded border border-slate-200"><HazardTag ev={ev} /></span>{ev.start_time}–{ev.end_time}</span>
    <span className={`justify-self-end rounded-full px-2 py-0.5 text-xs font-medium sm:justify-self-start ${PHASE[phase][1]}`} data-testid={`today-phase-${ev.id}`}>{PHASE[phase][0]}</span>
    <span className="col-span-2 min-w-0 sm:col-span-1">
      <span className="block truncate font-medium text-slate-800">{no(ev)} · {ev.company_name} · {jobType(ev)}</span>
      <span className="flex flex-wrap gap-x-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{ev.employee_count} kişi</span>
        <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{ev.equipment_text}</span>
        {ev.special_situations?.length > 0 && <span className="flex items-center gap-1 text-amber-700"><Siren className="h-3 w-3" />{ev.special_situations.map(s => s.title).join(', ')}</span>}
        {ev.job_description && <span className="truncate max-w-md">{ev.job_description}</span>}
      </span>
    </span>
    <span className="hidden text-xs text-slate-400 sm:inline">Detay ›</span>
  </button>
);

export const TodayOnSite = ({ filters, situationId, onView }) => {
  const [open, setOpen] = useState(() => localStorage.getItem('today-onsite-open') !== '0');
  const [items, setItems] = useState([]);
  const [tick, setTick] = useState(Date.now());
  const today = iso(new Date(tick));

  useEffect(() => { const t = setInterval(() => setTick(Date.now()), 60 * 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    axios.get(`${API}/work-permits/calendar`, { params: { start: today, end: today, statuses: 'aktif', ...filters } })
      .then(r => setItems(r.data)).catch(() => setItems([]));
  }, [today, JSON.stringify(filters)]);

  const now = new Date(tick).getHours() * 60 + new Date(tick).getMinutes();
  const list = (situationId === 'all' ? items : items.filter(ev => (ev.special_situations || []).some(s => s.id === situationId)))
    .map(ev => ({ ev, phase: phaseOf(ev, today, now) }));
  const groups = list.reduce((acc, x) => ({ ...acc, [x.ev.location_name]: [...(acc[x.ev.location_name] || []), x] }), {});
  const running = list.filter(x => x.phase === 'run').length;
  const toggle = () => { localStorage.setItem('today-onsite-open', open ? '0' : '1'); setOpen(!open); };

  return (
    <div className="rounded-xl border border-emerald-200 bg-white shadow-sm overflow-hidden" data-testid="today-onsite-panel">
      <button type="button" onClick={toggle} className="flex w-full items-center gap-3 bg-emerald-50 px-4 py-3 text-left" data-testid="today-onsite-toggle">
        <HardHat className="h-5 w-5 text-emerald-700" />
        <span className="font-semibold text-emerald-900">Bugün Sahada</span>
        <span className="text-sm text-emerald-700" data-testid="today-onsite-summary">{list.length} iş · {running} devam ediyor · {Object.keys(groups).length} lokasyon</span>
        <ChevronDown className={`ml-auto h-4 w-4 text-emerald-700 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (list.length === 0
        ? <p className="px-4 py-6 text-center text-sm text-slate-500" data-testid="today-onsite-empty">Bugün sahada onaylı iş bulunmuyor.</p>
        : Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, 'tr')).map(([loc, rows]) => (
          <div key={loc} className="border-t" data-testid={`today-group-${loc}`}>
            <p className="flex items-center gap-1.5 bg-slate-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600"><MapPin className="h-3.5 w-3.5" />{loc} <span className="font-normal normal-case">({rows.length})</span></p>
            <div className="divide-y">{rows.sort((a, b) => a.ev.start_time.localeCompare(b.ev.start_time)).map(x => <Row key={x.ev.id} ev={x.ev} phase={x.phase} onView={onView} />)}</div>
          </div>
        )))}
    </div>
  );
};
